import { NavLink, Outlet, Link } from 'react-router-dom';
import { Shield, Archive, BookOpen, Users, ArrowLeft, Link2, KeyRound, Palette, Building2, Database } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Shell for the administration area: a section rail plus an outlet.
 *
 * Sections are Archive, Instruction Library and Users.
 *
 * "Reference Forms" is specified (docs/Admin_Area_Specification.md §4) but is
 * Increment 2 and is deliberately absent — an empty section advertising a
 * capability that does not exist is worse than no section.
 *
 * "Test Data" was removed rather than kept: it was a development harness
 * ("Data Management Agent Test") whose default project id no longer existed,
 * and the data management plan it produced is generated through the normal
 * Data Management step. Giving it a permanent home dignified it beyond what
 * it was.
 *
 * Everything here renders only behind `AdminRoute`.
 */

const BASE_SECTIONS = [
  { to: 'users', label: 'Users', icon: Users },
  { to: 'sso', label: 'SSO', icon: KeyRound },
  { to: 'instruction-library', label: 'Instruction Library', icon: BookOpen },
  { to: 'archive', label: 'Archive', icon: Archive },
  // SparkVerify's internal reporting surface (spec §8). Internal only: it is
  // never marketed, demoed or sold, and it creates no client-facing surface.
  { to: 'chain', label: 'Chain', icon: Link2 },
  { to: 'organizations', label: 'Organizations', icon: Building2 },
  { to: 'db-spike', label: 'DB Spike', icon: Database },
];

export default function AdminLayout() {
  const { isSuperAdmin } = useAuth();
  const sections = isSuperAdmin
    ? [
        BASE_SECTIONS[0],
        BASE_SECTIONS[1],
        { to: 'branding', label: 'Branding', icon: Palette },
        ...BASE_SECTIONS.slice(2),
      ]
    : BASE_SECTIONS;
  return (
    <div className="min-h-screen bg-[#F7F9FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="border-b border-[#DCE2E5] bg-white">
        <div className="mx-auto flex max-w-[1440px] items-center gap-4 px-6 py-5 lg:px-10">
          <Link to="/" className="rounded-full p-2 text-[#5F696C] transition hover:bg-[#E6F4F2] hover:text-[var(--brand-primary)]" aria-label="Back to projects">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-[var(--brand-primary)]" />
            <div>
              <h1 className="text-xl font-bold text-[#001820]">Administration</h1>
              <p className="text-xs text-[#5F696C]">Platform governance and operations</p>
            </div>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1440px] gap-1 px-6 lg:px-10">
          {sections.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  isActive
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main>
        <Outlet />
      </main>
    </div>
  );
}
